import type { ReactNode } from "react";
import { Tag } from "lucide-react";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { InfoRow } from "@/shared/components/ui/InfoRow";
import { StatusBadge } from "@/shared/components/ui/StatusBadge";
import type { Service } from "../types/serviceTypes";
import { formatServiceMinutes, formatServicePrice } from "../utils/serviceFormatters";

interface ServiceRowProps {
  service: Service;
  onClick?: () => void;
  selected?: boolean;
  searchText?: string;
  showDescription?: boolean;
  bookedPrice?: number | null;
  bookedDurationMinutes?: number | null;
  actions?: ReactNode;
}

export function ServiceRow({
  service, onClick, selected, searchText, showDescription,
  bookedPrice, bookedDurationMinutes, actions,
}: ServiceRowProps) {
  const { business } = useCurrentBusiness();
  const price = bookedPrice === undefined ? service.basePrice : bookedPrice;
  const duration = bookedDurationMinutes === undefined ? service.durationMinutes : bookedDurationMinutes;

  return (
    <InfoRow
      title={service.name}
      icon={<Tag className="size-5 text-muted-foreground" style={{ color: service.color || undefined }} aria-hidden="true" />}
      subtitle={<span className="block whitespace-normal [overflow-wrap:anywhere]">
        {formatServicePrice(price, business?.currency)} / {duration === null ? "Duration not set" : formatServiceMinutes(duration)}
      </span>}
      caption={
        <>
          {showDescription && service.description && <span className="block">{service.description}</span>}
          <span>{formatServiceMinutes(service.bufferBeforeMinutes)} before / {formatServiceMinutes(service.bufferAfterMinutes)} after</span>
        </>
      }
      trailing={selected === undefined && <StatusBadge tone={service.isActive ? "positive" : "neutral"}>{service.isActive ? "Active" : "Inactive"}</StatusBadge>}
      actions={actions}
      density="compact"
      selected={selected}
      onClick={onClick}
      searchText={searchText}
    />
  );
}
